// Templates on the web (docs/45, docs/47: listed and used in version 1; made and changed in the apps): the library,
// grouped by area as on the iPhone (Templates.swift: TemplatesView), a template's page with its numbered steps, and
// the sheet that makes a project from it (UseTemplateSheet). Without Pro, the first two templates can be used; the
// others rest.

import { useState } from "react";
import type { Template, TemplateStep } from "../core/records";
import { isUsable, sectionTitles, templateGroups, whenFromStored, type TemplateWhen } from "../core/templates";
import { addDays } from "../core/days";
import { currentLanguage, t } from "../i18n";
import { useWindow } from "./context";
import { splitEmoji } from "./format";
import { Icon } from "./Icon";
import { Markdown } from "./Markdown";
import { Calendar } from "./Pickers";
import { Dialog } from "./Sheets";

/** "12 March". */
function usedOn(date: Date): string {
  return new Intl.DateTimeFormat(currentLanguage(), { day: "numeric", month: "long" }).format(date);
}

/** "6 steps · last used 12 March". */
export function templateSummary(template: Template): string {
  const steps = t("{count} steps", { count: template.steps.length });
  return template.lastUsedAt ? t("{steps} · last used {date}", { steps, date: usedOn(template.lastUsedAt) }) : steps;
}

function areaName(title: string): string {
  const { emoji, name } = splitEmoji(title);
  return [emoji, name].filter(Boolean).join(" ");
}

// MARK: - The library

export function TemplatesPage() {
  const { state, go, showSheet } = useWindow();
  const templates = state.records.templates;
  const use = (template: Template) => showSheet(isUsable(template, templates, state.isPro)
    ? { kind: "useTemplate", templateID: template.id }
    : { kind: "pro", feature: "templates" });
  return (
    <>
      <div className="list-heading templates-heading">
        <span className="icon-square"><Icon name="templates" /></span>
        <div>
          <h1 className="screen-title">{t("Templates")}</h1>
          <p className="heading-detail">{t("What you do now and then, written down once.")}</p>
        </div>
      </div>
      {templates.length === 0 ? (
        <div className="empty">
          <span className="icon-square"><Icon name="templates" /></span>
          <b>{t("Write It Down Once")}</b>
          {t("The filter that takes a special size, the pool every spring, the tax return: the steps, what you need, and what went wrong last time, ready for next time.")}
          <span className="empty-note">{t("Templates are made in the Todolix app on your iPhone, iPad or Mac: in a project that went well, ••• › Save as Template.")}</span>
        </div>
      ) : (
        templateGroups(templates, state.lists.sortedAreas).map((group) => (
          <section key={group.area?.id ?? "none"} className="template-group" aria-label={group.area ? areaName(group.area.title) : t("Templates")}>
            {group.area ? <h2 className="template-area">{areaName(group.area.title)}</h2> : <div className="template-gap" />}
            {group.templates.map((template) => {
              const usable = isUsable(template, templates, state.isPro);
              return (
                <div key={template.id} className={`template-row${usable ? "" : " resting"}`}>
                  <button className="template-open" onClick={() => go({ kind: "template", id: template.id })}>
                    <Icon name="templates" className="template-glyph" />
                    <span className="template-text">
                      <span className="template-title">{template.title || t("New Template")}</span>
                      <span className="template-summary">{usable ? templateSummary(template) : t("Rests without Pro")}</span>
                    </span>
                  </button>
                  <button className="outline-button small" onClick={() => use(template)} aria-label={t("Use {name}", { name: template.title })}>
                    {t("Use")}
                  </button>
                </div>
              );
            })}
          </section>
        ))
      )}
    </>
  );
}

// MARK: - One template

export function TemplatePage({ id }: { id: string }) {
  const { state, go, showSheet } = useWindow();
  const [open, setOpen] = useState<string | null>(null);
  const template = state.records.templates.find((one) => one.id === id);
  if (!template) {
    return (
      <div className="empty">
        <span className="icon-square"><Icon name="templates" /></span>
        <b>{t("This template is gone")}</b>
        <button className="link" onClick={() => go({ kind: "templates" })}>{t("All Templates")}</button>
      </div>
    );
  }
  const usable = isUsable(template, state.records.templates, state.isPro);
  const area = state.lists.area(template.areaID);
  const meta = [t("Template"), area ? areaName(area.title) : undefined, template.lastUsedAt ? t("last used {date}", { date: usedOn(template.lastUsedAt) }) : undefined]
    .filter(Boolean).join(" · ");
  const numbers = new Map(template.steps.map((step, index) => [step.id, index + 1]));
  const step = (item: TemplateStep) => (
    <StepRow key={item.id} step={item} number={numbers.get(item.id) ?? 0} open={open === item.id}
      onToggle={() => setOpen((current) => (current === item.id ? null : item.id))} />
  );
  return (
    <>
      <button className="crumb" onClick={() => go({ kind: "templates" })}><Icon name="chevLeft" />{t("Templates")}</button>
      <div className="list-heading templates-heading">
        <span className="icon-square"><Icon name="templates" /></span>
        <div>
          <h1 className="screen-title">{template.title || t("New Template")}</h1>
          <p className="heading-detail">{meta}</p>
        </div>
      </div>
      {template.notes && <div className="list-notes template-notes"><Markdown text={template.notes} /></div>}
      {!usable && (
        <button className="banner resting-note" onClick={() => showSheet({ kind: "pro", feature: "templates" })}>
          {t("Without Pro, two templates can be used and changed. This one waits, with everything in it, until Pro is back.")}
        </button>
      )}
      <div className="template-use">
        <button className="mint-button" onClick={() => showSheet(usable ? { kind: "useTemplate", templateID: template.id } : { kind: "pro", feature: "templates" })}>
          {t("Use Template")}
        </button>
      </div>
      <ol className="steps" aria-label={t("Steps")}>
        {template.steps.filter((item) => item.sectionTitle === undefined).map(step)}
        {sectionTitles(template).map((title) => (
          <li key={`section-${title}`} className="step-section">
            <h2>{title}</h2>
            <ol>{template.steps.filter((item) => item.sectionTitle === title).map(step)}</ol>
          </li>
        ))}
      </ol>
      <p className="template-footnote"><Icon name="info" />{t("Steps are written and changed in the Todolix app on your iPhone, iPad or Mac.")}</p>
    </>
  );
}

function StepRow({ step, number, open, onToggle }: { step: TemplateStep; number: number; open: boolean; onToggle: () => void }) {
  const hasMore = step.notes !== "" || step.checklist.length > 0;
  return (
    <li className={`step${open ? " open" : ""}`}>
      <button className="step-head" onClick={onToggle} aria-expanded={hasMore ? open : undefined} disabled={!hasMore}>
        <span className="step-number">{number}</span>
        <span className="step-title">{step.title || t("New Step")}</span>
        {!open && hasMore && (
          <span className="step-marks" aria-hidden="true">
            {step.notes !== "" && <Icon name="note" />}
            {step.checklist.length > 0 && <Icon name="checklist" />}
          </span>
        )}
      </button>
      {open && (
        <div className="step-body">
          {step.notes && <div className="step-notes"><Markdown text={step.notes} /></div>}
          {step.checklist.length > 0 && (
            <ul className="step-checklist">
              {step.checklist.map((item, index) => <li key={index}><span className="step-box" aria-hidden="true" />{item}</li>)}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

// MARK: - Use Template

export function UseTemplateSheet({ templateID, onClose }: { templateID: string; onClose: () => void }) {
  const { state, model, go } = useWindow();
  const template = state.records.templates.find((one) => one.id === templateID);
  const areas = state.lists.sortedAreas;
  const tomorrow = addDays(state.today, 1);
  // What was chosen last time, else the template's own area.
  const [name, setName] = useState("");
  const [areaID, setAreaID] = useState<string | undefined>(() =>
    state.lists.area(template?.lastAreaID)?.id ?? state.lists.area(template?.areaID)?.id ?? areas[0]?.id);
  const [when, setWhen] = useState<TemplateWhen>(() => whenFromStored(template?.lastWhen, state.today));
  if (!template) return null;
  const create = () => {
    const project = model.useTemplate(template.id, name, areaID, when);
    onClose();
    if (project) go({ kind: "project", id: project.id });
  };
  const chip = (label: string, chosen: boolean, choose: () => void, icon?: "today") => (
    <button role="radio" aria-checked={chosen} className={`choice-chip${chosen ? " chosen" : ""}`} onClick={choose}>
      {icon && <Icon name={icon} />}{label}
    </button>
  );
  return (
    <Dialog onClose={onClose}>
      <h2 id="sheet-title">{t("Use Template")}</h2>
      <label className="use-field">
        <span>{t("Name")}</span>
        <input className="field" value={name} placeholder={template.title} onChange={(event) => setName(event.target.value)} autoFocus
          onKeyDown={(event) => { if (event.key === "Enter") create(); }} />
      </label>
      {areas.length > 0 && (
        <div className="use-field">
          <span id="use-area">{t("Area")}</span>
          <div className="area-choices" role="radiogroup" aria-labelledby="use-area">
            {areas.map((area) => (
              <button key={area.id} role="radio" aria-checked={areaID === area.id} className={`area-choice${areaID === area.id ? " chosen" : ""}`}
                onClick={() => setAreaID(area.id)}>
                {areaName(area.title)}
                {areaID === area.id && <Icon name="check" />}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="use-field">
        <span id="use-when">{t("When")}</span>
        <div className="chips" role="radiogroup" aria-labelledby="use-when">
          {chip(t("Today"), when.kind === "today", () => setWhen({ kind: "today" }), "today")}
          {chip(t("Anytime"), when.kind === "anytime", () => setWhen({ kind: "anytime" }))}
          {chip(t("Pick a Day"), when.kind === "day", () => setWhen({ kind: "day", day: when.kind === "day" ? when.day : tomorrow }))}
        </div>
        {when.kind === "day" && (
          <div className="use-calendar">
            <Calendar selected={when.day} today={state.today} onDay={(day) => { if (day > state.today) setWhen({ kind: "day", day }); }} />
          </div>
        )}
      </div>
      <p className="use-note">{t("The template stays as it is. What you change in the project stays in the project, until you choose to keep it.")}</p>
      <div className="actions">
        <button className="outline-button" onClick={onClose}>{t("Cancel")}</button>
        <button className="mint-button" onClick={create}>{t("Create Project")}</button>
      </div>
    </Dialog>
  );
}
